import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface WorksheetItem {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
}

const worksheets: WorksheetItem[] = [
  { id: 'bc-lim-1', title: 'Evaluating Limits Analytically', description: 'Direct substitution, factoring, rationalizing, special limits', topic: 'Limits & Continuity', difficulty: 'Medium' },
  { id: 'bc-lim-2', title: 'Continuity, IVT & Limits at Infinity', description: 'Types of discontinuities, IVT, horizontal & vertical asymptotes', topic: 'Limits & Continuity', difficulty: 'Medium' },
  { id: 'bc-dif-1', title: 'Limit Definition of the Derivative', description: 'Difference quotient, tangent lines, differentiability conditions', topic: 'Differentiation', difficulty: 'Medium' },
  { id: 'bc-dif-2', title: 'Basic Differentiation Rules', description: 'Power, product, quotient, trig, exponential, and log rules', topic: 'Differentiation', difficulty: 'Easy' },
  { id: 'bc-adv-1', title: 'Chain Rule & Implicit Differentiation', description: 'Composites, implicit curves, second implicit derivatives', topic: 'Advanced Differentiation', difficulty: 'Hard' },
  { id: 'bc-adv-2', title: 'Inverse & Logarithmic Differentiation', description: 'Inverse trig, inverse function theorem, log differentiation', topic: 'Advanced Differentiation', difficulty: 'Hard' },
  { id: 'bc-app-1', title: 'Particle Motion & Related Rates', description: 'Position/velocity/acceleration, related rates problems', topic: 'Applications of Derivatives', difficulty: 'Hard' },
  { id: 'bc-app-2', title: "L'Hôpital's Rule & Linearization", description: "Indeterminate forms, L'Hôpital's Rule, tangent approximations", topic: 'Applications of Derivatives', difficulty: 'Medium' },
  { id: 'bc-ana-1', title: 'MVT, EVT & Optimization', description: 'Mean Value Theorem, absolute extrema, optimization problems', topic: 'Analytical Applications', difficulty: 'Hard' },
  { id: 'bc-ana-2', title: 'Curve Sketching & Concavity', description: 'First/second derivative tests, inflection points, curve analysis', topic: 'Analytical Applications', difficulty: 'Medium' },
  { id: 'bc-int-1', title: 'Antiderivatives, Riemann Sums & FTC', description: 'Integration basics, definite integrals, FTC Parts 1 & 2', topic: 'Integration', difficulty: 'Medium' },
  { id: 'bc-int-2', title: 'U-Substitution & Techniques', description: 'U-substitution, integration by parts, partial fractions', topic: 'Integration', difficulty: 'Hard' },
  { id: 'bc-de-1', title: 'Slope Fields & Separable DEs', description: 'Slope field visualization, solving separable DEs', topic: 'Differential Equations', difficulty: 'Hard' },
  { id: 'bc-de-2', title: "Euler's Method & Logistic Growth", description: "Euler's method approximation, logistic growth model", topic: 'Differential Equations', difficulty: 'Medium' },
  { id: 'bc-ai-1', title: 'Area Between Curves', description: 'Areas using horizontal and vertical slices', topic: 'Applications of Integration', difficulty: 'Medium' },
  { id: 'bc-ai-2', title: 'Volumes of Revolution', description: 'Disk, washer, and shell methods', topic: 'Applications of Integration', difficulty: 'Hard' },
  { id: 'bc-par-1', title: 'Parametric Equations & Calculus', description: 'dy/dx, d²y/dx², arc length for parametric curves', topic: 'Parametric & Vectors', difficulty: 'Hard' },
  { id: 'bc-par-2', title: 'Vector-Valued Functions', description: 'Position, velocity, acceleration, speed for vector functions', topic: 'Parametric & Vectors', difficulty: 'Hard' },
  { id: 'bc-pol-1', title: 'Polar Coordinates & Derivatives', description: 'Polar-rectangular conversions, slopes in polar', topic: 'Polar Curves', difficulty: 'Hard' },
  { id: 'bc-pol-2', title: 'Polar Areas & Arc Length', description: 'Area enclosed by polar curves, arc length formula', topic: 'Polar Curves', difficulty: 'Hard' },
  { id: 'bc-ser-1', title: 'Sequences & Convergence Tests', description: 'Geometric, p-series, divergence, integral, comparison tests', topic: 'Infinite Series', difficulty: 'Hard' },
  { id: 'bc-ser-2', title: 'Ratio, Root & Alternating Series Tests', description: 'Ratio test, root test, AST, absolute/conditional convergence', topic: 'Infinite Series', difficulty: 'Hard' },
  { id: 'bc-tay-1', title: 'Power Series & Radius of Convergence', description: 'Power series, interval/radius of convergence', topic: 'Taylor Series', difficulty: 'Hard' },
  { id: 'bc-tay-2', title: 'Taylor & Maclaurin Series', description: 'Deriving series for eˣ, sin x, cos x, 1/(1-x), applications', topic: 'Taylor Series', difficulty: 'Hard' },
];

const categories = [
  { id: 'Limits & Continuity', color: 'bg-teal-500' },
  { id: 'Differentiation', color: 'bg-amber-500' },
  { id: 'Advanced Differentiation', color: 'bg-rose-500' },
  { id: 'Applications of Derivatives', color: 'bg-green-500' },
  { id: 'Analytical Applications', color: 'bg-violet-500' },
  { id: 'Integration', color: 'bg-orange-500' },
  { id: 'Differential Equations', color: 'bg-sky-500' },
  { id: 'Applications of Integration', color: 'bg-pink-500' },
  { id: 'Parametric & Vectors', color: 'bg-lime-500' },
  { id: 'Polar Curves', color: 'bg-red-500' },
  { id: 'Infinite Series', color: 'bg-emerald-500' },
  { id: 'Taylor Series', color: 'bg-indigo-500' },
];

export default function APCalcBCProgressPage() {
  const [progress, setProgress] = useState<Record<string, boolean>>({});
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('ap-calc-bc-progress');
    if (saved) setProgress(JSON.parse(saved));
  }, []);

  const toggle = (id: string) => {
    const updated = { ...progress, [id]: !progress[id] };
    setProgress(updated);
    localStorage.setItem('ap-calc-bc-progress', JSON.stringify(updated));
  };

  const reset = () => {
    setProgress({});
    localStorage.removeItem('ap-calc-bc-progress');
    setShowReset(false);
  };

  const completed = Object.values(progress).filter(Boolean).length;
  const total = worksheets.length;
  const pct = Math.round((completed / total) * 100);

  const getCatProgress = (cat: string) => {
    const items = worksheets.filter(w => w.topic === cat);
    const done = items.filter(w => progress[w.id]).length;
    return { done, total: items.length, pct: items.length > 0 ? Math.round((done / items.length) * 100) : 0 };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <i className="ri-bar-chart-box-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AP Calculus BC Progress Tracker</h1>
              <p className="text-gray-600 mt-0.5">Track your mastery across all {total} skill areas</p>
            </div>
          </div>
          <button onClick={() => setShowReset(true)} className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 whitespace-nowrap cursor-pointer transition-colors">
            <i className="ri-restart-line"></i>Reset
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Overall Progress</h2>
              <p className="text-teal-50 mb-4">Master all 10 units to be ready for a 5 on the AP Calculus BC exam!</p>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold">{completed}</span>
                <span className="text-2xl text-teal-100">/ {total}</span>
                <span className="text-teal-100 ml-2">units completed</span>
              </div>
            </div>
            <div className="relative w-44 h-44 flex-shrink-0">
              <svg className="w-full h-full -rotate-90">
                <circle cx="88" cy="88" r="80" stroke="rgba(255,255,255,0.2)" strokeWidth="14" fill="none" />
                <circle cx="88" cy="88" r="80" stroke="white" strokeWidth="14" fill="none"
                  strokeDasharray={`${2 * Math.PI * 80}`}
                  strokeDashoffset={`${2 * Math.PI * 80 * (1 - pct / 100)}`}
                  strokeLinecap="round" className="transition-all duration-500" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold">{pct}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Progress by Unit</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map(cat => {
              const p = getCatProgress(cat.id);
              return (
                <div key={cat.id} className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className={`w-8 h-8 ${cat.color} rounded-lg flex items-center justify-center mb-2`}>
                    <i className="ri-book-2-line text-sm text-white"></i>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-xs mb-2 leading-tight">{cat.id}</h3>
                  <div className="flex items-baseline gap-1 mb-1.5">
                    <span className="text-xl font-bold text-gray-900">{p.done}</span>
                    <span className="text-gray-500 text-xs">/ {p.total}</span>
                    <span className="text-xs text-gray-500 ml-auto">{p.pct}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={`${cat.color} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${p.pct}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Full Checklist</h2>
          {categories.map(cat => {
            const items = worksheets.filter(w => w.topic === cat.id);
            const p = getCatProgress(cat.id);
            if (!items.length) return null;
            return (
              <div key={cat.id} className="mb-8 last:mb-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 ${cat.color} rounded-lg flex items-center justify-center`}>
                    <i className="ri-folder-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{cat.id}</h3>
                  <span className="text-sm text-gray-500">({p.done}/{items.length})</span>
                </div>
                <div className="space-y-3">
                  {items.map(item => {
                    const done = !!progress[item.id];
                    return (
                      <div key={item.id} className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${done ? 'bg-teal-50 border-teal-200' : 'bg-white border-gray-200'}`}>
                        <button onClick={() => toggle(item.id)}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all ${done ? `${cat.color} border-transparent` : 'border-gray-300 hover:border-gray-400'}`}>
                          {done && <i className="ri-check-line text-white text-xs"></i>}
                        </button>
                        <div className="flex-1">
                          <h4 className={`font-semibold mb-0.5 ${done ? 'line-through text-gray-400' : 'text-gray-900'}`}>{item.title}</h4>
                          <p className={`text-sm ${done ? 'line-through text-gray-400' : 'text-gray-600'}`}>{item.description}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${item.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : item.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{item.difficulty}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <Link to="/ap-calc-bc" className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all group cursor-pointer">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-book-open-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">BC Curriculum</h3>
            <p className="text-gray-600 text-sm">Practice all 640+ questions</p>
          </Link>
          <Link to="/ap-calc-bc-study-plan" className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all group cursor-pointer">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-calendar-check-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Study Plan</h3>
            <p className="text-gray-600 text-sm">Structured weekly schedule</p>
          </Link>
          <Link to="/ap-calc-ab" className="bg-white rounded-xl p-6 border border-gray-200 hover:border-amber-300 transition-all group cursor-pointer">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-arrow-left-circle-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">AP Calc AB</h3>
            <p className="text-gray-600 text-sm">Review AB foundations</p>
          </Link>
        </div>
      </div>

      {showReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-alert-line text-3xl text-red-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">Reset Progress?</h3>
            <p className="text-gray-600 text-center mb-6">This will clear all completed units. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowReset(false)} className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium whitespace-nowrap cursor-pointer transition-colors">Cancel</button>
              <button onClick={reset} className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium whitespace-nowrap cursor-pointer transition-colors">Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
